import { type MouseEvent } from 'react';

import { toast } from '@backyard-ui/core';
import {
  ProductThumb as ProductThumbComponent,
  type ProductThumbProduct,
} from '@leroy-merlin-br/backpack-product-thumb';
import { RatingStars } from '@leroy-merlin-br/backpack-rating-stars';

import { type ModularContent } from '@/infra/services/modular-content/model';
import { GtmDataAttributesGenerator } from '@/infra/tracking/gtm-data-attributes';
import { type Partner } from '@/infra/tracking/gtm-data-attributes/generators/productsList';
import * as wishlistTracker from '@/infra/tracking/tracker/wishlist/tracker';
import { useAddProductsToCart } from '@/presenters/hooks/checkout/cart';
import { CART_ID } from '@/utils/constants/cookies';
import { cookies } from '@/utils/cookies';

import { Wishlist } from '../Wishlist';
import { WishlistButton } from '../WishlistButton';

type Product = ModularContent.Product;

export interface ProductThumbProps extends Product {
  onClick?: (event: MouseEvent<HTMLAnchorElement>, product: Product) => void;
  rating: number;
  totalReviewsCount: number;
  isFetchingRatings: boolean;
  categoryName?: string;
  position?: number;
  gtmAttributes?: {
    partnerName?: Partner;
    title: string;
    position: number;
  };
}

function ProductThumb(props: ProductThumbProps) {
  const {
    onClick,
    rating,
    totalReviewsCount,
    isFetchingRatings,
    categoryName,
    position,
    gtmAttributes,
    ...product
  } = props;

  const { mutate, isLoading, isSuccess } = useAddProductsToCart({
    onSuccess: () => {
      toast({
        description: 'Produto adicionado ao carrinho',
        appearance: 'success',
      });
    },
  });

  const productForBackpack: ProductThumbProduct = {
    ...product,
    price: {
      ...product.price,
      discount: product.price.discount
        ? {
            ...product.price.discount,
            labels: {
              ...product.price.discount.labels,
              offer: 'Oferta',
            },
          }
        : undefined,
    },
  };

  return (
    <ProductThumbComponent
      product={productForBackpack}
      rating={
        <RatingStars
          rating={rating}
          totalReviewsCount={totalReviewsCount}
          isFetchingRatings={isFetchingRatings}
        />
      }
      wishlist={
        <Wishlist
          item={{ id: product.id, type: 'products' }}
          trigger={WishlistButton}
          onCreate={(listName) => {
            wishlistTracker.createWishlist(listName);
            wishlistTracker.addProductToWishlist({ product, listName });
          }}
          onAdd={(listName) =>
            wishlistTracker.addProductToWishlist({ product, listName })
          }
          onRemove={() => wishlistTracker.removeProductFromWishlist(product)}
        />
      }
      onClick={(event) => onClick?.(event, product)}
      onClickAddToCart={({ id }) =>
        mutate({
          cartId: cookies.client.get(CART_ID)?.value || '',
          type: 'delivery',
          products: [{ id, quantity: 1 }],
        })
      }
      isLoadingAddToCart={isLoading}
      isSuccessAddToCart={isSuccess}
      {...GtmDataAttributesGenerator.productsList({
        partnerName: gtmAttributes?.partnerName || 'algolia',
        item: product,
        title: gtmAttributes?.title || `entry-key-${categoryName}`,
        position: gtmAttributes?.position || position || 0,
      })}
    />
  );
}

export default ProductThumb;
